import { Link } from "react-router-dom";
import { Sparkles, Frame, FileText, Share2, ArrowRight, Lock } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { usePageMeta } from "@/lib/usePageMeta";

const TOOLKITS = [
  {
    title: "Black Girl Joy Challenge",
    tag: "2026 · with Pete & Thomas Foundation",
    desc: "Make your winner frame, build a shareable page, and get a personal press release.",
    to: "/bgjcpt",
    live: true,
    color: "#AD1C5D",
  },
  { title: "Black Girls Dream Fund", tag: "Coming soon", desc: "Toolkit for Dream Fund grant recipients.", live: false, color: "#400057" },
  { title: "Environment & Climate Fund", tag: "Coming soon", desc: "Toolkit for Environment & Climate Fund grant recipients.", live: false, color: "#67DAD7" },
  { title: "Dream Investment Program", tag: "Coming soon", desc: "Toolkit for Dream Investment Program participants.", live: false, color: "#E02189" },
];

const STEPS = [
  { icon: Frame, title: "Make your frame", body: "Upload a photo, pick an SBG or Pete & Thomas frame, drag to fit, and download." },
  { icon: Share2, title: "Get your page", body: "A shareable winner page is created automatically — your photo and name show in the preview." },
  { icon: FileText, title: "Share your news", body: "Get a ready-to-send personal press release for your local news outlets." },
];

export default function Hub() {
  usePageMeta({ title: "Southern Black Girls Toolkits", description: "Winner toolkits from Southern Black Girls and Women's Consortium: frames, shareable pages, and press releases." });
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteNav />

      {/* hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 joy-dotgrid opacity-70" />
        <div className="relative mx-auto max-w-6xl px-5 py-12 lg:py-16">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-sbg-burgundy px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-sbg-blush">
              <Sparkles className="h-3.5 w-3.5" /> Winner Toolkits
            </span>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-black leading-[1.05] tracking-tight text-sbg-burgundy text-balance sm:text-5xl lg:text-6xl">
              Turn your win into <span className="text-sbg-fuchsia">joy</span> your whole community can see.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-sbg-burgundy/80 sm:text-lg">
              Welcome, Joy Maker. These toolkits from Southern Black Girls and Women's Consortium help our winners and
              grantees celebrate, share their news, and spread the word.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/bgjcpt"
                data-testid="hero-start-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-sbg-fuchsia px-6 py-3.5 text-base font-bold text-white shadow-[0_12px_30px_rgba(224,33,137,0.35)] transition-transform hover:-translate-y-1"
              >
                Start the Joy Challenge toolkit
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* steps */}
      <section className="mx-auto max-w-6xl px-5 pb-8">
        <div className="grid gap-4 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <div
              key={s.title}
              className="animate-fade-up rounded-3xl bg-white p-7 ring-1 ring-sbg-raspberry/10"
              style={{ animationDelay: `${i * 90}ms` }}
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sbg-blush text-sbg-raspberry">
                <s.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-4 text-xl font-bold text-sbg-burgundy">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-sbg-burgundy/70">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* toolkits */}
      <section className="mx-auto max-w-6xl px-5 py-10">
        <h2 className="font-display text-2xl font-bold tracking-tight text-sbg-burgundy sm:text-3xl">Choose your toolkit</h2>
        <div className="mt-7 grid gap-4 sm:grid-cols-2">
          {TOOLKITS.map((t) => {
            const inner = (
              <div
                className={`relative h-full overflow-hidden rounded-3xl p-7 ring-1 transition-all ${
                  t.live ? "bg-white ring-sbg-raspberry/10 hover:-translate-y-1 hover:shadow-2xl" : "bg-white/60 ring-sbg-raspberry/5"
                }`}
              >
                <span className="h-2 w-16 rounded-full" style={{ background: t.color, display: "block" }} />
                <div className="mt-4 flex items-center gap-2">
                  <h3 className="text-xl font-bold text-sbg-burgundy">{t.title}</h3>
                  {!t.live && <Lock className="h-4 w-4 text-sbg-burgundy/40" />}
                </div>
                <p className="mt-1 text-xs font-semibold uppercase tracking-wider" style={{ color: t.color }}>
                  {t.tag}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-sbg-burgundy/70">{t.desc}</p>
                {t.live && (
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-sbg-fuchsia">
                    Open toolkit <ArrowRight className="h-4 w-4" />
                  </span>
                )}
              </div>
            );
            return t.live ? (
              <Link key={t.title} to={t.to} data-testid="toolkit-card-bgjc" className="block">
                {inner}
              </Link>
            ) : (
              <div key={t.title}>{inner}</div>
            );
          })}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
